import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, X, Download, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Modal, SelectField, Badge } from "./UI";

const MODES = [
  { value: "create_update", label: "Create & Update (buat baru + perbarui)" },
  { value: "update_only", label: "Update-only (hanya perbarui yang ada)" },
  { value: "create_only", label: "Create-only (hanya buat item baru)" },
];

const ACTION_TONE = {
  NEW: "green",
  UPDATE: "blue",
  NOCHANGE: "neutral",
  ERROR: "amber",
};

/**
 * Item Export -> Edit -> Import dialog with 2-step preview (dry-run) then commit.
 * Read-only columns (id, stock, cost) are ignored on import. Blank cells keep existing values.
 */
export function ItemImportDialog({ onClose, onSuccess }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("create_update");
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // {summary, rows, total}
  const [result, setResult] = useState(null);

  const resetPreview = () => {
    setPreview(null);
    setResult(null);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) {
      toast.error("File harus berformat CSV");
      return;
    }
    setFile(f);
    resetPreview();
  };

  const exportItems = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/items/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "items_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Item berhasil diexport ke CSV");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const runPreview = async () => {
    if (!file) return toast.error("Pilih file CSV dulu");
    setPreviewing(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post("/items/import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post("/items/import/commit", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Import selesai: ${data.created} baru, ${data.updated} diperbarui`);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCommitting(false);
    }
  };

  const s = preview?.summary;
  const applicable = s ? s.new + s.update : 0;

  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "true" : "false";
    return String(v);
  };

  return (
    <Modal title="Import & Export Item (CSV)" eyebrow="Export → Edit → Import" onClose={onClose}>
      <div className="upload-body" data-testid="item-import-dialog">
        <div className="upload-instructions">
          <p className="eyebrow">Langkah 1 · Export item saat ini</p>
          <div className="upload-note">
            Unduh seluruh item ke CSV, edit di Excel/Sheets, lalu upload kembali. Kolom{" "}
            <b>id</b>, <b>stock</b>, dan <b>cost</b> bersifat read-only dan <b>diabaikan</b> saat import.
            Sel kosong akan mempertahankan nilai lama.
          </div>
          <button
            type="button"
            className="secondary-button"
            data-testid="item-export-button"
            onClick={exportItems}
            disabled={exporting}
          >
            <Download size={15} /> {exporting ? "Mengekspor..." : "Export item ke CSV"}
          </button>
        </div>

        <div className="upload-instructions">
          <p className="eyebrow">Langkah 2 · Pilih mode & file</p>
          <SelectField
            label="Mode import"
            value={mode}
            options={MODES}
            onChange={(v) => {
              setMode(v);
              resetPreview();
            }}
            testid="item-import-mode"
          />

          <div
            className={`dropzone ${file ? "has-file" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files?.[0]);
            }}
            data-testid="item-import-dropzone"
          >
            <Upload size={22} />
            <strong>{file ? file.name : "Klik atau tarik file CSV ke sini"}</strong>
            <span>Format .csv</span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              data-testid="item-import-file-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {file && (
              <button
                type="button"
                className="chip small danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  resetPreview();
                }}
              >
                <X size={12} /> Hapus
              </button>
            )}
          </div>
        </div>

        {preview && (
          <div className="upload-preview" data-testid="item-import-preview">
            <p className="eyebrow">Langkah 3 · Pratinjau (dry-run) — belum disimpan</p>
            <div className="result-summary" style={{ marginBottom: 10 }}>
              <span className="chip green small" data-testid="preview-new-count">
                <Check size={12} /> {s.new} baru
              </span>
              <span className="chip blue small" data-testid="preview-update-count">
                {s.update} update
              </span>
              <span className="chip small" data-testid="preview-nochange-count">
                {s.nochange} tanpa perubahan
              </span>
              <span className="chip amber small" data-testid="preview-error-count">
                <AlertTriangle size={12} /> {s.error} error
              </span>
            </div>
            <div className="table-wrap" style={{ maxHeight: 320, overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Baris</th>
                    <th>SKU</th>
                    <th>Aksi</th>
                    <th>Perubahan / Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} data-testid={`preview-row-${r.row}`}>
                      <td>{r.row}</td>
                      <td>{r.sku || "—"}</td>
                      <td>
                        <Badge tone={ACTION_TONE[r.action] || "neutral"}>{r.action}</Badge>
                      </td>
                      <td>
                        {r.action === "ERROR" ? (
                          <span style={{ color: "var(--amber)" }}>{r.errors.join("; ")}</span>
                        ) : Object.keys(r.changes).length ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {Object.entries(r.changes).map(([k, v]) => (
                              <span key={k} className="chip small">
                                {k}: {fmt(v.old)} → <b>{fmt(v.new)}</b>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: "var(--muted)" }}>Tidak ada perubahan</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="upload-result" data-testid="item-import-result">
            <p className="eyebrow">Hasil import</p>
            <div className="result-summary">
              <span className="chip green small">
                <Check size={12} /> {result.created} baru
              </span>
              <span className="chip blue small">{result.updated} diperbarui</span>
              {result.errors?.length > 0 && (
                <span className="chip amber small">{result.errors.length} error</span>
              )}
            </div>
            {result.errors?.length > 0 && (
              <ul className="error-list">
                {result.errors.slice(0, 15).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onClose}>
            Tutup
          </button>
          {!result && (
            <button
              type="button"
              className="secondary-button"
              disabled={!file || previewing}
              onClick={runPreview}
              data-testid="item-import-preview-button"
            >
              <RefreshCw size={15} /> {previewing ? "Memproses..." : preview ? "Pratinjau ulang" : "Pratinjau"}
            </button>
          )}
          {preview && !result && (
            <button
              type="button"
              className="primary-button"
              disabled={committing || applicable === 0}
              onClick={commit}
              data-testid="item-import-commit-button"
            >
              <FileText size={16} />{" "}
              {committing ? "Menyimpan..." : `Terapkan ${applicable} perubahan`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
